import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DragDropModule, CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { ShowUtilisateursEtiquetteDialogComponent } from '../../etiquettes-managment/show-utilisateurs-etiquette-dialog/show-utilisateurs-etiquette-dialog.component';

import { ParcoursFormationService } from 'src/services/parcours-formation.service';
import { ScormService } from 'src/services/scorm.service';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { ParcoursFormationDetailPilotageModel, PackageScormAvecOrdrePilotageModel } from 'src/models/pilotage/parcoursFormationDetailPilotageModel';
import { ScormPackageLocaleModel, ScormPackageModel } from 'src/models/scormPackageModel';
import { PackageCardComponent } from '../../bibliotheques-managment/package-card/package-card.component';
import { ContexteParcoursFormation } from 'src/models/contexteParcoursFormationModel';
import { EtiquettePilotageModel } from 'src/models/pilotage/etiquettePilotageModel';
import { UtilisateurListPilotageModel } from 'src/models/pilotage/utilisateurListPilotageModel';
import { EtiquetteService } from 'src/services/etiquette.service';
import { PreventDoubleClickDirective } from 'src/directives/prevent-double-click.directive';

@Component({
    selector: 'app-create-edit-parcours-formation',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatProgressSpinnerModule,
        TranslateModule,
        NgxMatSelectSearchModule,
        MatFormFieldModule,
        DragDropModule,
        MatIconModule,
        MatDialogModule,
        PackageCardComponent,
        PreventDoubleClickDirective
    ],
    templateUrl: './create-edit-parcours-formation.component.html',
    styleUrls: ['./create-edit-parcours-formation.component.scss']
})
export class CreateEditParcoursFormationComponent implements OnInit, OnDestroy {
  isLoading = true;
  isSaving = false;
  isCreationMode: boolean;
  parcoursFormationId: number | null = null;

  supportedLanguages = [
    { code: 'fr', label: 'LEARNING_PATH_MANAGEMENT.NAME_FR' },
    { code: 'en', label: 'LEARNING_PATH_MANAGEMENT.NAME_EN' },
    { code: 'es', label: 'LEARNING_PATH_MANAGEMENT.NAME_ES' }
  ];
  locales: { [lang: string]: string } = { fr: '', en: '', es: '' };

  recurrenceOptions = [1, 2, 3, 4, 5];
  estRecurrent: boolean = false;
  recurrenceEnAnnees: number | null = null;

  parcoursFormation: ParcoursFormationDetailPilotageModel = {} as ParcoursFormationDetailPilotageModel;
  packagesInParcoursFormation: PackageScormAvecOrdrePilotageModel[] = [];
  availablePackages: ScormPackageModel[] = [];

  contexte: ContexteParcoursFormation = {} as ContexteParcoursFormation;
  etiquettesDisponibles: EtiquettePilotageModel[] = [];
  utilisateursDisponibles: UtilisateurListPilotageModel[] = [];

  selectedEtiquettes: EtiquettePilotageModel[] = [];
  selectedUtilisateurs: UtilisateurListPilotageModel[] = [];

  etiquetteFilterCtrl = new FormControl();
  utilisateurFilterCtrl = new FormControl();

  filteredEtiquettesSearch = new ReplaySubject<any[]>(1);
  filteredUtilisateursSearch = new ReplaySubject<any[]>(1);

  searchInPath: string = '';
  searchAvailable: string = '';
  private _allPackagesInPath: any[] = [];
  private _allAvailablePackages: any[] = [];

  protected _onDestroy = new Subject<void>();

  constructor(
    public parcoursFormationService: ParcoursFormationService,
    public etiquetteService: EtiquetteService,
    private scormService: ScormService,
    private utilisateurService: UtilisateurService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {
    const id = this.route.snapshot.paramMap.get('id');
    this.parcoursFormationId = id ? Number(id) : null;
    this.isCreationMode = !this.parcoursFormationId;
  }

  ngOnInit() {
    this.loadContexte();
    this.setupFilterSubscriptions();
    this.bindingFilter();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  exporterListeContenusCompletes() {
    const entiteId = this.utilisateurService.entiteUtilisateurSelectionnee.entiteId;
    const codeLangue = this.translate.currentLang || 'fr';
    this.scormService.exporterListeContenusCompletes(entiteId, codeLangue).subscribe(blob => {
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const datetime = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const filename = `contenus_completes_${datetime}.xlsx`;
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
    });
  }

  private loadContexte() {
    this.parcoursFormationService.obtenirContexte().subscribe(contexte => {
      this.contexte = contexte;
      this.etiquettesDisponibles = contexte.etiquettesDisponibles.sort((a, b) =>
        this.etiquetteService.getNomLocalise(a.locales)?.localeCompare(
          this.etiquetteService.getNomLocalise(b.locales)));
      this.utilisateursDisponibles = contexte.utilisateursDisponibles.sort((a, b) => a.nom?.localeCompare(b.nom));

      if (this.isCreationMode) {
        this.initializeNewParcours();
      } else {
        this.loadExistingParcours();
      }
    });
  }

  private initializeNewParcours() {
    this.locales = { fr: '', en: '', es: '' };
    this.estRecurrent = false;
    this.recurrenceEnAnnees = null;
    this.parcoursFormation = {
      parcoursFormationId: 0,
      locales: this.locales,
      scormPackages: [],
      etiquettesDisponibles: this.etiquettesDisponibles,
      utilisateursDisponibles: this.utilisateursDisponibles,
      etiquettesSelectionnees: [],
      utilisateursSelectionnes: [],
      totalPackages: 0,
      dateCreation: new Date(),
      estRecurrent: false,
      recurrenceEnAnnees: null,
      isMenuOpen: false
    } as ParcoursFormationDetailPilotageModel;

    this._allPackagesInPath = [];
    this.loadAvailablePackages();
    this.filterUtilisateurs();
    this.filterEtiquettes();
    this.filteredUtilisateursSearch.next(this.utilisateursDisponibles);
    this.filteredEtiquettesSearch.next(this.etiquettesDisponibles);
    this.isLoading = false;
  }

  private loadExistingParcours() {
    if (!this.parcoursFormationId) return;

    this.parcoursFormationService.obtenirParcoursFormation(this.parcoursFormationId)
      .subscribe(parcours => {
        this.parcoursFormation = parcours;

        this.locales = { fr: '', en: '', es: '', ...parcours.locales };
        this.estRecurrent = parcours.estRecurrent ?? false;
        this.recurrenceEnAnnees = parcours.recurrenceEnAnnees ?? null;

        this.packagesInParcoursFormation = parcours.scormPackages;
        this._allPackagesInPath = [...parcours.scormPackages];

        this.selectedEtiquettes = parcours.etiquettesSelectionnees
          .map(id => this.etiquettesDisponibles.find(e => e.etiquetteId === id))
          .filter(e => e !== undefined) as EtiquettePilotageModel[];

        this.selectedUtilisateurs = parcours.utilisateursSelectionnes
          .map(id => this.utilisateursDisponibles.find(u => u.utilisateurId === id))
          .filter(u => u !== undefined) as UtilisateurListPilotageModel[];

        this.loadAvailablePackages();
        this.filterUtilisateurs();
        this.filterEtiquettes();
        this.isLoading = false;
      });
  }

  private loadAvailablePackages() {
    const entiteId = this.utilisateurService.entiteUtilisateurSelectionnee.entiteId;
    this.scormService.getAllPackages(entiteId).subscribe(packages => {
      const filtered = packages.filter(pkg =>
        !this.packagesInParcoursFormation.some(p => p.package.scormPackageId === pkg.scormPackageId)
      );
      this.availablePackages = this.sortPackagesAlphabetically(filtered);
      this._allAvailablePackages = this.sortPackagesAlphabetically(filtered);
    });
  }

  private bindingFilter() {
    this.etiquetteFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterEtiquettes();
      });

    this.utilisateurFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterUtilisateurs();
      });
  }

  private setupFilterSubscriptions() {
    this.etiquetteFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((etiquetteId: number) => {
        if (etiquetteId) {
          const etiquette = this.etiquettesDisponibles
            .find(e => e.etiquetteId === etiquetteId);
          if (etiquette) {
            this.selectedEtiquettes.push(etiquette);
            this.parcoursFormation.etiquettesSelectionnees.push(etiquetteId);
            this.etiquetteFilterCtrl.setValue('', { emitEvent: false });
            this.filterEtiquettes();
          }
        } else {
          this.filterEtiquettes();
        }
      });

    this.utilisateurFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((utilisateurId: number) => {
        if (utilisateurId) {
          const utilisateur = this.utilisateursDisponibles
            .find(u => u.utilisateurId === utilisateurId);
          if (utilisateur) {
            this.selectedUtilisateurs.push(utilisateur);
            this.parcoursFormation.utilisateursSelectionnes = [
              ...this.parcoursFormation.utilisateursSelectionnes || [],
              utilisateur.utilisateurId
            ];
            this.utilisateurFilterCtrl.setValue('', { emitEvent: false });
            this.filterUtilisateurs();
          }
        } else {
          this.filterUtilisateurs();
        }
      });
  }

  filterPackagesInPath() {
    if (!this.searchInPath.trim()) {
      this.packagesInParcoursFormation = [...this._allPackagesInPath];
      return;
    }
    this.packagesInParcoursFormation = this._allPackagesInPath.filter(pkg =>
      this.getPackageTitle(pkg.package?.locales).toLowerCase().includes(this.searchInPath.toLowerCase())
    );
  }

  filterAvailablePackages() {
    if (!this.searchAvailable.trim()) {
      this.availablePackages = this.sortPackagesAlphabetically([...this._allAvailablePackages]);
      return;
    }
    const filtered = this._allAvailablePackages.filter(pkg =>
      this.getPackageTitle(pkg.locales).toLowerCase().includes(this.searchAvailable.toLowerCase())
    );
    this.availablePackages = this.sortPackagesAlphabetically(filtered);
  }

  clearPackages() {
    this.availablePackages = [...this._allAvailablePackages, ...this.packagesInParcoursFormation];
    this.packagesInParcoursFormation = [];
    this._allPackagesInPath = [];
  }

  onDrop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      if (event.container.data === this.packagesInParcoursFormation) {
        this.updateOrdreAffichage();
      }
    } else {
      const draggedItem = event.previousContainer.data[event.previousIndex];

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.handleTransferredItem(event);

      if (event.container.data === this.packagesInParcoursFormation) {
        const wrappedItem = event.container.data[event.currentIndex];
        if (!this._allPackagesInPath.some(p => p.package.scormPackageId === wrappedItem.package.scormPackageId)) {
          this._allPackagesInPath.push(wrappedItem);
        }
        this._allAvailablePackages = this._allAvailablePackages.filter(p => p.scormPackageId !== draggedItem.scormPackageId);
      } else if (event.container.data === this.availablePackages) {
        this._allPackagesInPath = this._allPackagesInPath.filter(p => p.package.scormPackageId !== draggedItem.package.scormPackageId);
        if (!this._allAvailablePackages.some(p => p.scormPackageId === draggedItem.package.scormPackageId)) {
          this._allAvailablePackages.push(draggedItem.package);
        }
      }
    }
  }

  private handleTransferredItem(event: CdkDragDrop<any[]>) {
    if (event.container.data === this.packagesInParcoursFormation) {
      const transferredItem = event.container.data[event.currentIndex];
      if (!('package' in transferredItem)) {
        event.container.data[event.currentIndex] = {
          package: transferredItem,
          ordreAffichage: this.packagesInParcoursFormation.length
        };
      }
    } else if (event.container.data === this.availablePackages) {
      const transferredItem = event.container.data[event.currentIndex];
      if ('package' in transferredItem) {
        event.container.data[event.currentIndex] = transferredItem.package;
      }
    }
    this.updateOrdreAffichage();
  }

  private updateOrdreAffichage() {
    this.packagesInParcoursFormation.forEach((item, index) => {
      item.ordreAffichage = index + 1;
    });
  }

  isValid(): boolean {
    const hasTitle = Object.values(this.locales).some(val => val && val.trim());
    const hasPackages = this.packagesInParcoursFormation.length > 0;
    const hasAssignment = this.selectedEtiquettes.length > 0 || this.selectedUtilisateurs.length > 0;
    return hasTitle && hasPackages && hasAssignment && this.hasRecurrenceInterval();
  }

  hasRecurrenceInterval(): boolean {
    if (!this.estRecurrent) {
      return true;
    }
    return !!this.recurrenceEnAnnees && this.recurrenceEnAnnees > 0;
  }

  onEstRecurrentChange() {
    if (!this.estRecurrent) {
      this.recurrenceEnAnnees = null;
    }
  }

  save() {
    if (!this.isValid() || this.isSaving) {
      return;
    }

    this.isSaving = true;

    const model = {
      parcoursFormationId: this.parcoursFormationId || 0,
      locales: this.locales,
      estRecurrent: this.estRecurrent,
      recurrenceEnAnnees: this.estRecurrent ? this.recurrenceEnAnnees : null,
      packages: this._allPackagesInPath.map(p => ({
        scormPackageId: p.package.scormPackageId,
        ordreAffichage: p.ordreAffichage
      })),
      etiquetteIdAssignes: this.selectedEtiquettes.map(e => e.etiquetteId),
      utilisateurIdAssignes: this.selectedUtilisateurs.map(u => u.utilisateurId)
    };

    const action = this.isCreationMode ?
      this.parcoursFormationService.creerParcoursFormation(model) :
      this.parcoursFormationService.modifierParcoursFormation(model);

    action.subscribe(() => {
      this.router.navigate(['/parcours-formation-managment']);
    }, () => {
      this.isSaving = false;
    });
  }
  cancel() {
    this.router.navigate(['/parcours-formation-managment']);
  }

  private filterEtiquettes() {
    if (!this.etiquettesDisponibles) return;
    const search = this.etiquetteFilterCtrl.value?.toLowerCase() || '';

    const filtered = this.etiquettesDisponibles.filter(etiquette =>
      !this.selectedEtiquettes.some(selected => selected.etiquetteId === etiquette.etiquetteId) &&
      this.etiquetteService.getNomLocalise(etiquette.locales)
        .toLowerCase()
        .includes(search)
    );

    this.filteredEtiquettesSearch.next(filtered);
  }

  public filterUtilisateurs() {
    if (!this.utilisateursDisponibles) return;
    const search = this.utilisateurFilterCtrl.value?.toLowerCase() || '';

    const filtered = this.utilisateursDisponibles.filter(utilisateur =>
      !this.selectedUtilisateurs.some(selected => selected.utilisateurId === utilisateur.utilisateurId) &&
      utilisateur.nom.toLowerCase().includes(search)
    );

    this.filteredUtilisateursSearch.next(filtered);
  }

  removeEtiquette(etiquetteId: number): void {
    this.selectedEtiquettes = this.selectedEtiquettes
      .filter(e => e.etiquetteId !== etiquetteId);
    this.parcoursFormation.etiquettesSelectionnees = this.parcoursFormation.etiquettesSelectionnees
      .filter(id => id !== etiquetteId);
    this.filterEtiquettes();
  }

  removeUtilisateur(utilisateurId: number) {
    this.selectedUtilisateurs = this.selectedUtilisateurs.filter(user => user.utilisateurId !== utilisateurId);
    this.parcoursFormation.utilisateursSelectionnes = this.parcoursFormation.utilisateursSelectionnes
      .filter(id => id !== utilisateurId);
    this.filterUtilisateurs();
  }

  showUtilisateursEtiquette(etiquette: EtiquettePilotageModel): void {
    this.etiquetteService.obtenirUtilisateursParEtiquette(etiquette.etiquetteId).subscribe(
      (utilisateurs) => {
        this.dialog.open(ShowUtilisateursEtiquetteDialogComponent, {
          width: '50vw',
          maxWidth: '800px',
          disableClose: true,
          data: { etiquette: etiquette, utilisateurs: utilisateurs }
        });
      },
      () => {
        this.dialog.open(ShowUtilisateursEtiquetteDialogComponent, {
          width: '50vw',
          maxWidth: '800px',
          disableClose: true,
          data: { etiquette: etiquette, utilisateurs: [] }
        });
      }
    );
  }

  getPackageTitle(locales: ScormPackageLocaleModel[] | undefined): string {
    if (!locales) return '';
    const frTitle = locales.find(l => l.langue === 'fr')?.titre || '';
    const enTitle = locales.find(l => l.langue === 'en')?.titre || '';
    const esTitle = locales.find(l => l.langue === 'es')?.titre || '';
    return this.parcoursFormationService.getNomLocaliseFromStrings(frTitle, enTitle, esTitle);
  }

  private getDisplayName(pkg: ScormPackageModel): string {
    const title = this.getPackageTitle(pkg.locales);
    return !pkg.actif ? `[INACTIVE] ${title}` : title;
  }

  private sortPackagesAlphabetically(packages: ScormPackageModel[]): ScormPackageModel[] {
    return [...packages].sort((a, b) => {
      const nameA = this.getDisplayName(a).toLowerCase();
      const nameB = this.getDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  hasTitle(): boolean {
    return Object.values(this.locales).some(val => val && val.trim());
  }

  hasPackages(): boolean {
    return this.packagesInParcoursFormation.length > 0;
  }

  hasAssignment(): boolean {
    return this.selectedEtiquettes.length > 0 || this.selectedUtilisateurs.length > 0;
  }
}
